import React, { useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { COMPANY_ID, MSG_SUCESSO, enviarOrcamento } from '../lib/crm';

// Formulário de orçamento no padrão do CRM (docs/formulariopadrao.md).

const TIPOS_EVENTO = [
  // Social
  'Casamento',
  'Aniversário',
  '15 anos',
  'Formatura',
  'Bodas',
  'Batizado',
  'Chá revelação',
  // Corporativo
  'Confraternização',
  'Feira',
  'Congresso',
  'Convenção',
  'Coffee break',
  'Lançamento de produto',
  'Treinamento',
  'Outro',
];

const VALORES_INICIAIS = {
  nome: '',
  telefone: '',
  email: '',
  segmento: '',
  nome_empresa: '',
  tipo_evento: '',
  quantitativo: '',
  descricao: '',
  consentimento: false,
};

const CAMPOS_VALIDADOS = [
  'nome',
  'telefone',
  'email',
  'segmento',
  'nome_empresa',
  'tipo_evento',
  'quantitativo',
  'consentimento',
];

const SEM_MENSAGEM = { text: '', isError: false };

const ANTI_SPAM_KEY = 'sofistic:lastFormSubmitAtMs';
const ANTI_SPAM_WINDOW_MS = 2 * 60 * 60 * 1000;

const PLANILHA_ENDPOINT =
  process.env.REACT_APP_GAS_WEBAPP_URL ||
  'https://script.google.com/macros/s/AKfycbzFuoYFQPP61Rk7qa86TWeCM-9wUH0bzhWHClU3owQHQGWlYQlaDmKpuFbZJk_7pac6Tg/exec';

// (81) 99999-0000 / (81) 3333-0000. Aceita colar com +55.
function mascaraTelefone(valor) {
  let d = String(valor || '').replace(/\D/g, '');
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);
  d = d.slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// Texto sem espaços nas pontas; vazio vira null.
function texto(valor) {
  const v = String(valor ?? '').trim();
  return v === '' ? null : v;
}

// Cópia do pedido para a planilha antiga (Google Apps Script), com as mesmas
// chaves de antes. Só roda depois que o CRM aceitou o pedido e não muda o que
// o visitante vê.
function enviarPlanilha(dados) {
  fetch(PLANILHA_ENDPOINT, {
    method: 'POST',
    mode: 'no-cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      nome: dados.p_nome,
      telefone: dados.p_telefone,
      email: dados.p_email,
      tipoEvento: dados.p_segmento, // nesta planilha a coluna sempre foi Social/Corporativo
      quantitativo: dados.p_quantitativo ?? '',
      descricao: dados.p_descricao ?? '',
      consentimento: dados.p_consentimento,
    }),
  }).catch((err) => console.error('[orçamento] falha no envio para a planilha', err));
}

export default function FormularioSection({ title = 'Solicite um orçamento', className = '' }) {
  const colors = useMemo(
    () => ({
      black: '#000000',
      white: '#FFFFFF',
      gold: '#e3d992',
      glassBg: 'rgba(255, 255, 255, 0.06)',
      glassBorder: 'rgba(227, 217, 146, 0.25)',
    }),
    []
  );

  const prefersReducedMotion = useReducedMotion();

  const [values, setValues] = useState(VALORES_INICIAIS);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(SEM_MENSAGEM);
  const honeypotRef = useRef(null);

  const isCorporativo = values.segmento === 'corporativo';

  const errors = useMemo(() => {
    const next = {};

    if (!values.nome.trim()) next.nome = 'Informe seu nome.';

    const digits = values.telefone.replace(/\D/g, '');
    if (!digits) next.telefone = 'Informe seu telefone.';
    else if (digits.length < 10) next.telefone = 'Informe um telefone válido (DDD + número).';

    if (!values.email.trim()) next.email = 'Informe seu e-mail.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      next.email = 'Informe um e-mail válido.';
    }

    if (!values.segmento) next.segmento = 'Selecione o segmento.';

    if (isCorporativo && !values.nome_empresa.trim()) {
      next.nome_empresa = 'Informe o nome da empresa.';
    }

    if (!values.tipo_evento.trim()) next.tipo_evento = 'Informe o tipo de evento.';

    if (!values.quantitativo.trim()) next.quantitativo = 'Informe o quantitativo.';

    if (!values.consentimento) next.consentimento = 'É necessário autorizar o contato para prosseguir.';

    return next;
  }, [values, isCorporativo]);

  const isValid = Object.keys(errors).length === 0;

  const setField = (key) => (e) => {
    const { type, value, checked } = e.target;
    setValues((prev) => ({
      ...prev,
      [key]: type === 'checkbox' ? checked : value,
    }));
  };

  const setTelefone = (e) => {
    const telefone = mascaraTelefone(e.target.value);
    setValues((prev) => ({ ...prev, telefone }));
  };

  const markTouched = (key) => () => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const showError = (key) => Boolean(touched[key] && errors[key]);

  const errorProps = (key) => ({
    'aria-invalid': showError(key),
    'aria-describedby': showError(key) ? `orc-${key}-err` : undefined,
  });

  const renderError = (key) =>
    showError(key) && (
      <div id={`orc-${key}-err`} className="form-error" role="alert">
        {errors[key]}
      </div>
    );

  const limparFormulario = () => {
    setValues(VALORES_INICIAIS);
    setTouched({});
    if (honeypotRef.current) honeypotRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Honeypot preenchido = robô: finge sucesso e não envia nada.
    if (honeypotRef.current?.value) {
      limparFormulario();
      setSubmitResult({ text: MSG_SUCESSO, isError: false });
      return;
    }

    try {
      const lastSubmitAtMs = Number(window.localStorage.getItem(ANTI_SPAM_KEY) || '0');
      if (lastSubmitAtMs && Date.now() - lastSubmitAtMs < ANTI_SPAM_WINDOW_MS) {
        setSubmitResult({
          text: 'Você já enviou uma solicitação recentemente. Aguarde 2 horas para enviar novamente.',
          isError: true,
        });
        return;
      }
    } catch (_err) {
      // ignore localStorage issues
    }

    setTouched(Object.fromEntries(CAMPOS_VALIDADOS.map((key) => [key, true])));

    if (!isValid) {
      setSubmitResult(SEM_MENSAGEM);
      return;
    }

    // Exatamente as 10 chaves que o CRM espera (seção 4 do formulariopadrao.md).
    const dados = {
      p_company_id: COMPANY_ID,
      p_nome: texto(values.nome),
      p_telefone: texto(values.telefone),
      p_email: texto(values.email),
      p_quantitativo: texto(values.quantitativo),
      p_segmento: texto(values.segmento),
      p_nome_empresa: isCorporativo ? texto(values.nome_empresa) : null,
      p_tipo_evento: texto(values.tipo_evento),
      p_descricao: texto(values.descricao),
      p_consentimento: values.consentimento === true,
    };

    setSubmitting(true);
    setSubmitResult(SEM_MENSAGEM);

    try {
      await enviarOrcamento(dados);
    } catch (err) {
      setSubmitResult({ text: err.message, isError: true }); // o formulário NÃO é limpo
      return;
    } finally {
      setSubmitting(false);
    }

    // Daqui para baixo, o CRM aceitou o pedido.
    enviarPlanilha(dados);

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'lead_form_submit',
        form_name: 'Solicite um orçamento',
        business_name: 'Sofistic Buffet',
      });
    }

    try {
      window.localStorage.setItem(ANTI_SPAM_KEY, String(Date.now()));
    } catch (_err) {
      // ignore localStorage issues
    }

    limparFormulario();
    setSubmitResult({ text: MSG_SUCESSO, isError: false });
  };

  const sectionStyles = useMemo(
    () => ({
      width: '100%',
      background: 'transparent',
      padding: '4rem 1.5rem',
    }),
    []
  );

  const containerStyles = useMemo(
    () => ({
      maxWidth: '64rem',
      margin: '0 auto',
    }),
    []
  );

  const motionProps = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 18 },
        whileInView: { opacity: 1, y: 0 },
        transition: { duration: 0.6, ease: 'easeOut' },
        viewport: { once: true, amount: 0.35 },
      };

  return (
    <section
      id="orcamento"
      style={sectionStyles}
      className={className}
      aria-label="Formulário de orçamento"
    >
      <style>{`
        .form-header {
          margin: 0 0 2rem;
          min-height: 4rem;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .form-title {
          margin: 0;
          font-family: 'Playfair Display', serif;
          font-weight: 700;
          line-height: 1.1;
          letter-spacing: -0.02em;
          font-size: clamp(1.7rem, 4.4vw, 2.65rem);
          background: linear-gradient(180deg, #f6efbf 0%, #e3d992 55%, #c9b878 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }

        .form-title::after {
          content: '';
          display: block;
          width: 80px;
          height: 2px;
          margin: 0.85rem auto 0;
          background: linear-gradient(90deg, transparent, rgba(227, 217, 146, 0.85), transparent);
          border-radius: 2px;
        }

        .form-card {
          position: relative;
          background:
            radial-gradient(ellipse at top right, rgba(227, 217, 146, 0.08), transparent 38%),
            linear-gradient(180deg, rgba(8, 8, 8, 0.94) 0%, rgba(0, 0, 0, 0.95) 100%);
          border: 1px solid rgba(227, 217, 146, 0.45);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(227, 217, 146, 0.08) inset;
          border-radius: 1.5rem;
          padding: 1.75rem 1.25rem;
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          overflow: hidden;
        }

        .form-card::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 1.5rem;
          padding: 1px;
          background: linear-gradient(135deg, rgba(227, 217, 146, 0.55), rgba(227, 217, 146, 0) 38%, rgba(227, 217, 146, 0) 62%, rgba(227, 217, 146, 0.4));
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(1, minmax(0, 1fr));
          gap: 1rem;
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }

        .form-label {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 600;
          letter-spacing: 0.01em;
          font-size: 0.95rem;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          color: ${colors.white};
          background: rgba(0, 0, 0, 0.4);
          border: 1px solid rgba(227, 217, 146, 0.22);
          border-radius: 0.9rem;
          padding: 0.95rem 1rem;
          outline: none;
          color-scheme: dark;
          box-shadow: 0 0 0 rgba(0,0,0,0);
          transition:
            border-color 280ms cubic-bezier(0.22, 1, 0.36, 1),
            box-shadow 280ms cubic-bezier(0.22, 1, 0.36, 1),
            background 280ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .form-input:hover,
        .form-textarea:hover {
          border-color: rgba(227, 217, 146, 0.42);
        }

        .form-textarea {
          resize: vertical;
          min-height: 3rem;
        }

        .form-input::placeholder,
        .form-textarea::placeholder {
          color: rgba(255, 255, 255, 0.45);
        }

        .form-input:focus,
        .form-textarea:focus {
          border-color: rgba(227, 217, 146, 0.85);
          box-shadow: 0 0 0 4px rgba(227, 217, 146, 0.18), 0 8px 24px rgba(227, 217, 146, 0.08);
          background: rgba(0, 0, 0, 0.5);
        }

        .form-error {
          color: rgba(255, 192, 192, 0.95);
          font-size: 0.9rem;
          line-height: 1.25;
        }

        .form-fieldset {
          border: 1px solid rgba(227, 217, 146, 0.18);
          border-radius: 1rem;
          padding: 1rem;
        }

        .form-fieldset.is-compact {
          width: fit-content;
          max-width: 100%;
          justify-self: start;
        }

        .segmento-fieldset {
          margin: 0;
          min-width: 0;
          padding: 0.35rem 1rem 0.85rem;
        }

        .segmento-fieldset .form-options {
          margin-top: 0.35rem;
        }

        .segmento-fieldset .form-error {
          margin-top: 0.6rem;
        }

        .form-reveal {
          animation: form-reveal 320ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        @keyframes form-reveal {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }

        .form-hp {
          position: absolute;
          left: -9999px;
          width: 1px;
          height: 1px;
          overflow: hidden;
        }

        .form-legend {
          padding: 0 0.5rem;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 700;
          letter-spacing: 0.01em;
        }

        .form-options {
          margin-top: 0.75rem;
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          justify-content: flex-start;
        }

        .form-radio {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.65rem 0.95rem;
          border-radius: 999px;
          border: 1px solid rgba(227, 217, 146, 0.25);
          background: rgba(0, 0, 0, 0.3);
          color: rgba(255, 255, 255, 0.92);
          width: fit-content;
          cursor: pointer;
          transition: border-color 280ms cubic-bezier(0.22, 1, 0.36, 1), background 280ms cubic-bezier(0.22, 1, 0.36, 1), transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .form-radio:hover {
          border-color: rgba(227, 217, 146, 0.55);
          background: rgba(227, 217, 146, 0.06);
          transform: translateY(-1px);
        }

        .form-radio:has(input:checked) {
          border-color: rgba(227, 217, 146, 0.85);
          background: rgba(227, 217, 146, 0.1);
          box-shadow: 0 8px 22px rgba(227, 217, 146, 0.12);
        }

        .form-radio input {
          accent-color: ${colors.gold};
        }

        .form-consent {
          display: flex;
          align-items: flex-start;
          gap: 0.7rem;
          padding: 0.75rem 0.9rem;
          border-radius: 1rem;
          background: rgba(0, 0, 0, 0.22);
          border: 1px solid rgba(227, 217, 146, 0.18);
          color: rgba(255, 255, 255, 0.92);
        }

        .form-consent.is-compact {
          width: fit-content;
          max-width: 100%;
        }

        .form-consent-wrap {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.45rem;
        }

        .form-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .form-footer .form-consent-wrap {
          flex: 1 1 auto;
        }

        .form-footer .form-actions {
          margin-top: 0;
          justify-content: flex-end;
        }

        .form-consent span {
          line-height: 1.35;
          font-size: 0.95rem;
        }

        .form-consent input {
          margin-top: 0.2rem;
          accent-color: ${colors.gold};
        }

        .form-actions {
          display: flex;
          justify-content: center;
          margin-top: 1.25rem;
        }

        .form-btn {
          appearance: none;
          border: 1px solid rgba(227, 217, 146, 0.85);
          background: linear-gradient(135deg, #f6efbf 0%, #e3d992 55%, #c9b878 100%);
          color: #1a1a1a;
          border-radius: 999px;
          padding: 0.95rem 1.6rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-size: 0.82rem;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          box-shadow: 0 14px 30px rgba(227, 217, 146, 0.22), 0 0 0 1px rgba(255, 255, 255, 0.25) inset;
          transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 280ms cubic-bezier(0.22, 1, 0.36, 1), filter 280ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .form-btn::before {
          content: '';
          position: absolute;
          inset: -40% -60%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
          transform: translateX(-60%) rotate(12deg);
          transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .form-btn:hover {
          transform: translateY(-3px) scale(1.02);
          filter: brightness(1.04);
          box-shadow: 0 22px 50px rgba(227, 217, 146, 0.34), 0 0 0 1px rgba(255, 255, 255, 0.4) inset;
        }

        .form-btn:hover::before {
          transform: translateX(60%) rotate(12deg);
        }

        .form-btn:active {
          transform: translateY(-1px) scale(1);
        }

        .form-btn:focus-visible {
          outline: 2px solid ${colors.gold};
          outline-offset: 3px;
        }

        .form-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
          filter: none;
        }

        .form-result {
          margin-top: 1.25rem;
          text-align: center;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.35;
        }

        .form-result:empty {
          margin-top: 0;
        }

        .form-result.is-error {
          color: rgba(255, 192, 192, 0.95);
        }

        @media (min-width: 640px) {
          .form-card {
            padding: 2.25rem 2rem;
          }

          .form-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 1.15rem;
          }

          .span-2 {
            grid-column: 1 / -1;
          }

          .form-footer {
            flex-wrap: nowrap;
            align-items: center;
          }

          .form-footer .form-consent-wrap {
            flex: 1;
          }

          .form-footer .form-actions {
            flex: 0 0 auto;
          }

          .form-actions {
            justify-content: flex-end;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .form-input,
          .form-textarea,
          .form-btn {
            transition: none;
          }

          .form-btn::before {
            transition: none;
          }

          .form-reveal {
            animation: none;
          }
        }
      `}</style>

      <div style={containerStyles}>
        <header className="form-header">
          <h2 className="form-title">{title}</h2>
        </header>

        <motion.div className="form-card" {...motionProps}>
          <form onSubmit={handleSubmit} noValidate aria-busy={submitting}>
            <div className="form-grid">
              <div className="form-field">
                <label className="form-label" htmlFor="orc-nome">Nome</label>
                <input
                  id="orc-nome"
                  name="nome"
                  className="form-input"
                  type="text"
                  autoComplete="name"
                  required
                  maxLength={150}
                  placeholder="Seu nome"
                  value={values.nome}
                  onChange={setField('nome')}
                  onBlur={markTouched('nome')}
                  {...errorProps('nome')}
                />
                {renderError('nome')}
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="orc-telefone">Telefone</label>
                {/* Sem maxLength: a máscara já limita a 15 caracteres e assim dá para colar "+55 81 9..." */}
                <input
                  id="orc-telefone"
                  name="telefone"
                  className="form-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  required
                  placeholder="(00) 00000-0000"
                  value={values.telefone}
                  onChange={setTelefone}
                  onBlur={markTouched('telefone')}
                  {...errorProps('telefone')}
                />
                {renderError('telefone')}
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="orc-email">E-mail</label>
                <input
                  id="orc-email"
                  name="email"
                  className="form-input"
                  type="email"
                  autoComplete="email"
                  required
                  maxLength={200}
                  placeholder="seu@email.com"
                  value={values.email}
                  onChange={setField('email')}
                  onBlur={markTouched('email')}
                  {...errorProps('email')}
                />
                {renderError('email')}
              </div>

              <fieldset className="form-fieldset segmento-fieldset">
                <legend className="form-legend">Segmento</legend>
                <div className="form-options" onBlur={markTouched('segmento')}>
                  <label className="form-radio">
                    <input
                      type="radio"
                      name="segmento"
                      value="social"
                      required
                      checked={values.segmento === 'social'}
                      onChange={setField('segmento')}
                      {...errorProps('segmento')}
                    />
                    Social
                  </label>

                  <label className="form-radio">
                    <input
                      type="radio"
                      name="segmento"
                      value="corporativo"
                      checked={values.segmento === 'corporativo'}
                      onChange={setField('segmento')}
                      {...errorProps('segmento')}
                    />
                    Corporativo
                  </label>
                </div>
                {renderError('segmento')}
              </fieldset>

              {/* Só aparece com Segmento = Corporativo */}
              {isCorporativo && (
                <div className="form-field span-2 form-reveal">
                  <label className="form-label" htmlFor="orc-empresa">Nome da empresa</label>
                  <input
                    id="orc-empresa"
                    name="nome_empresa"
                    className="form-input"
                    type="text"
                    autoComplete="organization"
                    required
                    maxLength={200}
                    placeholder="Nome da empresa"
                    value={values.nome_empresa}
                    onChange={setField('nome_empresa')}
                    onBlur={markTouched('nome_empresa')}
                    {...errorProps('nome_empresa')}
                  />
                  {renderError('nome_empresa')}
                </div>
              )}

              <div className="form-field">
                <label className="form-label" htmlFor="orc-tipo">Tipo de evento</label>
                <input
                  id="orc-tipo"
                  name="tipo_evento"
                  className="form-input"
                  type="text"
                  list="orc-tipos"
                  autoComplete="off"
                  required
                  maxLength={100}
                  placeholder="Ex.: casamento, aniversário, congresso"
                  value={values.tipo_evento}
                  onChange={setField('tipo_evento')}
                  onBlur={markTouched('tipo_evento')}
                  {...errorProps('tipo_evento')}
                />
                <datalist id="orc-tipos">
                  {TIPOS_EVENTO.map((tipo) => (
                    <option key={tipo} value={tipo} />
                  ))}
                </datalist>
                {renderError('tipo_evento')}
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="orc-qtd">Quantitativo</label>
                <input
                  id="orc-qtd"
                  name="quantitativo"
                  className="form-input"
                  type="text"
                  required
                  maxLength={60}
                  placeholder="Nº de convidados (ex.: 150)"
                  value={values.quantitativo}
                  onChange={setField('quantitativo')}
                  onBlur={markTouched('quantitativo')}
                  {...errorProps('quantitativo')}
                />
                {renderError('quantitativo')}
              </div>

              <div className="form-field span-2">
                <label className="form-label" htmlFor="orc-descricao">Descrição</label>
                <textarea
                  id="orc-descricao"
                  name="descricao"
                  className="form-textarea"
                  rows={4}
                  maxLength={5000}
                  placeholder="Conte sobre o evento: data, local, horário, preferências..."
                  value={values.descricao}
                  onChange={setField('descricao')}
                />
              </div>

              {/* Armadilha para robôs: invisível para pessoas. NÃO remova. */}
              <div className="form-hp" aria-hidden="true">
                <label htmlFor="orc-website">Não preencha este campo</label>
                <input
                  id="orc-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  defaultValue=""
                  ref={honeypotRef}
                />
              </div>

              <div className="span-2 form-footer">
                <div className="form-consent-wrap">
                  <label className="form-consent is-compact">
                    <input
                      type="checkbox"
                      name="consentimento"
                      required
                      checked={values.consentimento}
                      onChange={setField('consentimento')}
                      onBlur={markTouched('consentimento')}
                      {...errorProps('consentimento')}
                    />
                    <span>
                      Autorizo o contato para fins comerciais conforme a Política de Privacidade
                    </span>
                  </label>
                  {renderError('consentimento')}
                </div>

                <div className="form-actions">
                  <button className="form-btn" type="submit" disabled={submitting}>
                    {submitting ? 'Enviando...' : 'Enviar'}
                  </button>
                </div>
              </div>
            </div>

            <div className={`form-result${submitResult.isError ? ' is-error' : ''}`} role="status">
              {submitResult.text}
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
